<template>
  <div class="distributors-page">
    <!-- Информация о текущем договоре -->
    <div class="agreement-status-section mb-4">
      <b-alert v-if="hasActiveAgreement" show variant="success" class="agreement-info">
        <div class="d-flex align-items-center">
          <!-- <b-icon icon="check-circle-fill" class="me-2" /> -->
          <div>
            <h5 class="mb-1">
              {{ $t('distributors.currentAgreement', 'Текущий договор') }}
            </h5>
            <p class="mb-0">
              {{ $t('distributors.agreementActive', 'Корабль обслуживается по договору') }}
              <strong>{{ deviceStore.getSelectedAgreementName }}</strong>
              .
              {{ $t('distributors.canChangeDistributor', 'При желании Вы можете сменить дистрибьютора, выбрав из списка
              ниже.') }}
            </p>
          </div>
        </div>
      </b-alert>
  
      <b-alert v-else show variant="warning" class="no-agreement-info">
        <div class="d-flex align-items-center">
          <!-- <b-icon icon="exclamation-triangle-fill" class="me-2" /> -->
          <div>
            <h5 class="mb-1">
              {{ $t('distributors.noAgreement', 'Договор не подключен') }}
            </h5>
            <p class="mb-0">
              {{ $t('distributors.needAgreement', 'Данный корабль не подключен к договору. Для того, чтобы использовать
              все сервисы и покупать карты, нужно выбрать дистрибьютора из списка ниже.') }}
            </p>
          </div>
        </div>
      </b-alert>
    </div>
  
    <!-- Описание сервиса -->
    <div class="service-description mb-4">
      <p class="lead">
        {{ $t('distributors.serviceDescription', 'Для доступа к сервису ECDIS-Link по покупке, доставке и управлению
        электронными навигационными картами (ENC) пользователям необходимо заключить договор с одним из официальных
        дистрибьюторов.') }}
      </p>
      <p class="text-muted">
        {{ $t('distributors.partnersInfo', 'Наши авторизованные партнеры обеспечивают соответствие регуляторным стандартам
        и предоставляют локализованную поддержку для беспрепятственного управления картами и активации сервиса.') }}
      </p>
    </div>
  
    <!-- Заголовок списка дистрибьюторов -->
    <div class="distributors-header mb-4">
      <h4 class="mb-2">
        <b-icon icon="building" class="me-2" />
        {{ $t('distributors.listTitle', 'Сертифицированные дистрибьюторы') }}
      </h4>
      <p class="text-muted">
        {{ $t('distributors.listDescription', 'Выберите дистрибьютора для заключения договора на обслуживание') }}
      </p>
    </div>
  
    <!-- Список дистрибьюторов в виде карточек -->
    <div class="distributors-grid">
      <b-row>
        <b-col v-for="distributor in distributors" :key="distributor.id" lg="6" xl="4" class="mb-4">
          <b-card class="distributor-card h-100" :class="{ 'current-distributor': isCurrentDistributor(distributor) }">
            <template #header>
              <div class="d-flex justify-content-between align-items-start">
                <h5 class="mb-0 distributor-name">
                  {{ distributor.name }}
                </h5>
                <b-badge v-if="isCurrentDistributor(distributor)" variant="success" class="current-badge">
                  {{ $t('distributors.current', 'Текущий') }}
                </b-badge>
              </div>
            </template>
  
            <div class="distributor-info">
              <!-- Адрес -->
              <div class="info-item mb-3">
                <b-icon icon="geo-alt-fill" class="text-muted me-2" />
                <span class="address">{{ distributor.address }}</span>
              </div>
  
              <!-- Поставщики данных -->
              <div class="info-item mb-3">
                <b-icon icon="shop" class="text-primary me-2" />
                <strong>{{ $t('distributors.dataProvider', 'Data Provider') }}:</strong>
                {{ distributor.providers }}
              </div>
  
              <!-- Языки -->
              <div class="info-item mb-3">
                <b-icon icon="translate" class="text-info me-2" />
                <strong>{{ $t('distributors.languages', 'Языки') }}:</strong>
                {{ distributor.languages }}
              </div>
  
              <!-- Контакты -->
              <div class="contacts-section">
                <div v-if="distributor.phone" class="contact-item mb-2">
                  <b-icon icon="telephone-fill" class="text-success me-2" />
                  <a :href="`tel:${distributor.phone}`" class="contact-link">{{ distributor.phone }}</a>
                </div>
  
                <div v-if="distributor.mobile" class="contact-item mb-2">
                  <b-icon icon="phone" class="text-success me-2" />
                  <a :href="`tel:${distributor.mobile}`" class="contact-link">{{ distributor.mobile }}</a>
                </div>
  
                <div v-if="distributor.email" class="contact-item mb-2">
                  <b-icon icon="envelope-fill" class="text-primary me-2" />
                  <a :href="`mailto:${distributor.email}`" class="contact-link">{{ distributor.email }}</a>
                </div>
  
                <div v-if="distributor.website" class="contact-item mb-3">
                  <b-icon icon="globe" class="text-secondary me-2" />
                  <a :href="`https://${distributor.website}`" target="_blank" class="contact-link">{{ distributor.website
                    }}</a>
                </div>
              </div>
            </div>
  
            <template #footer>
              <!-- <b-button :variant="isCurrentDistributor(distributor) ? 'outline-success' : 'primary'" size="sm" block
                              @click="sendRequest(distributor)">
                              <b-icon :icon="isCurrentDistributor(distributor) ? 'check-circle' : 'envelope'" class="me-2" />
                              {{ isCurrentDistributor(distributor) ?
                              $t('distributors.currentContract', 'Действующий договор') :
                              $t('distributors.sendRequest', 'Отправить заявку')
                              }}
                            </b-button> -->
            </template>
          </b-card>
        </b-col>
      </b-row>
    </div>
  
    <!-- Дополнительная информация -->
    <div class="additional-info mt-5">
      <b-card class="info-card">
        <template #header>
          <h5 class="mb-0">
            <b-icon icon="info-circle-fill" class="me-2" />
            {{ $t('distributors.additionalInfo', 'Дополнительная информация') }}
          </h5>
        </template>
        <div class="row">
          <div class="col-md-6">
            <h6>{{ $t('distributors.withAgreement', 'С договором') }}</h6>
            <ul class="feature-list">
              <li>
                <b-icon icon="check-circle-fill" class="text-success me-2" />
                {{ $t('distributors.fullAccess', 'Полный
                доступ к сервисам') }}
              </li>
              <li>
                <b-icon icon="check-circle-fill" class="text-success me-2" />
                {{ $t('distributors.creditPayment', 'Оплата в
                кредит') }}
              </li>
              <li>
                <b-icon icon="check-circle-fill" class="text-success me-2" />
                {{ $t('distributors.technicalSupport', 'Техническая поддержка') }}
              </li>
            </ul>
          </div>
          <div class="col-md-6">
            <h6>{{ $t('distributors.withoutAgreement', 'Без договора') }}</h6>
            <ul class="feature-list">
              <li>
                <b-icon icon="x-circle-fill" class="text-danger me-2" />
                {{ $t('distributors.limitedAccess', 'Ограниченный
                доступ') }}
              </li>
              <li>
                <b-icon icon="check-circle-fill" class="text-warning me-2" />
                {{ $t('distributors.cardOnly', 'Только оплата
                картой') }}
              </li>
              <li>
                <b-icon icon="x-circle-fill" class="text-danger me-2" />
                {{ $t('distributors.noSupport', 'Базовая
                поддержка') }}
              </li>
            </ul>
          </div>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import { mapStores } from 'pinia'
import { useDeviceStore } from '@/stores/device'

export default {
  name: 'Distributors',
  computed: {
    ...mapStores(useDeviceStore),

    hasActiveAgreement() {
      return this.deviceStore.getSelectedAgreementID && this.deviceStore.getSelectedAgreementName
    },

    distributors() {
      return [
        {
          id: 'geophone',
          name: 'GeoPhone Group International OY',
          address: 'Ristiniementie 24 C 11, Espoo FI-02320, Finland',
          providers: 'PRIMAR',
          languages: 'Finnish, English',
          phone: null,
          mobile: null,
          email: 'info@geophone.fi',
          website: 'geophone.fi',
          agreementKeywords: ['geophone', 'finland']
        },
        {
          id: 'gms',
          name: 'GMS Electronic Communications and Maritime Industry & Trade Ltd.',
          address: 'İçmeler Neighborhood, Çağdaş Street No: 1, Vema Tuzla Block C, Floor 9, Tuzla, Istanbul 34947, Turkey',
          providers: 'PRIMAR',
          languages: 'Turkish, English',
          phone: '+90 216 766 69 67',
          mobile: '+90 537 528 94 65',
          email: 'onur@gmsmaritime.com',
          website: 'gmsmaritime.com',
          agreementKeywords: ['gms', 'turkey', 'maritime']
        },
        {
          id: 'bestikon',
          name: 'Bestikon ERP Solutions LLC',
          address: 'Estonia',
          providers: 'PRIMAR',
          languages: 'English, Russian',
          phone: '+372 517 9035',
          mobile: null,
          email: 'mihhail@bestikon.ee',
          website: 'bestikon.ee',
          agreementKeywords: ['bestikon', 'estonia', 'erp']
        },
        {
          id: 'cauli',
          name: 'Cauli and Brocc Pty Ltd',
          address: '14-18 Reid Avenue, Westmead, 2145, NSW, Australia',
          providers: 'PRIMAR',
          languages: 'English, Russian',
          phone: '+61 450 120 287',
          mobile: null,
          email: 'getenc@cauliandbrocc.com',
          website: null,
          agreementKeywords: ['cauli', 'brocc', 'australia']
        }
      ]
    }
  },

  methods: {
    isCurrentDistributor(distributor) {
      if (!this.hasActiveAgreement) return false

      const agreementName = this.deviceStore.getSelectedAgreementName?.toLowerCase() || ''

      // Проверяем, содержит ли название договора ключевые слова дистрибьютора
      return distributor.agreementKeywords.some(keyword => agreementName.includes(keyword.toLowerCase()))
    },

    sendRequest(distributor) {
      if (this.isCurrentDistributor(distributor)) {
        this.$bvToast.toast(this.$t('distributors.alreadyConnected', 'У вас уже есть активный договор с этим дистрибьютором'), {
          title: this.$t('distributors.info', 'Информация'),
          variant: 'info',
          solid: true
        })
        return
      }

      // Открываем почтовый клиент с подготовленным письмом
      const subject = encodeURIComponent(this.$t('distributors.emailSubject', 'Заявка на заключение договора ECDIS-Link'))
      const body = encodeURIComponent(
        `${this.$t('distributors.emailGreeting', 'Здравствуйте!')}\n\n` +
        `${this.$t('distributors.emailBody', 'Меня интересует заключение договора на обслуживание корабля через сервис ECDIS-Link.')}\n\n` +
        `${this.$t('distributors.shipInfo', 'Информация о корабле:')}\n` +
        `${this.$t('distributors.deviceId', 'Device ID')}: ${this.deviceStore.getDeviceID || 'N/A'}\n` +
        `${this.$t('distributors.shipName', 'Название корабля')}: ${this.deviceStore.getShipName || 'N/A'}\n\n` +
        `${this.$t('distributors.emailClosing', 'С уважением')}`
      )

      const mailtoLink = `mailto:${distributor.email}?subject=${subject}&body=${body}`
      window.open(mailtoLink)

      this.$bvToast.toast(this.$t('distributors.requestSent', 'Заявка подготовлена. Проверьте ваш почтовый клиент.'), {
        title: this.$t('distributors.success', 'Успешно'),
        variant: 'success',
        solid: true
      })
    }
  }
}
</script>

<style scoped>
.distributors-page {
  padding: 1rem;
  font-family: 'Inter', sans-serif;
}

/* Секция статуса договора */
.agreement-status-section {
  margin-bottom: 2rem;
}

.agreement-info,
.no-agreement-info {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.agreement-info h5 {
  font-size: 1.125rem;
  font-weight: 600;
}

.agreement-info p {
  font-size: 0.9rem;
  font-weight: normal;
}

.no-agreement-info h5 {
  font-size: 1.125rem;
  font-weight: 600;
}

.no-agreement-info p {
  font-size: 0.9rem;
  font-weight: normal;
}

.agreement-info {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  border-left: 4px solid #28a745;
}

.no-agreement-info {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border-left: 4px solid #ffc107;
}

/* Описание сервиса */
.service-description {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  border-left: 4px solid #007bff;
}

.service-description .lead {
  font-size: 1rem;
  font-weight: 500;
}

.service-description p.text-muted {
  font-size: 0.875rem;
  font-weight: normal;
}

/* Заголовок списка */
.distributors-header {
  text-align: center;
}

.distributors-header h4 {
  font-size: 1.25rem;
  font-weight: 600;
}

.distributors-header p {
  font-size: 0.9rem;
  font-weight: normal;
}

/* Сетка дистрибьюторов */
.distributors-grid {
  margin-top: 2rem;
}

/* Карточки дистрибьюторов */
.distributor-card {
  border: 1px solid #e0e6ed;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  background: #ffffff;
}

.distributor-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.current-distributor {
  border: 2px solid #28a745;
  background: linear-gradient(135deg, #ffffff 0%, #f8fff9 100%);
}

.current-distributor::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(45deg, #28a745, #20c997);
  border-radius: 14px;
  z-index: -1;
}

/* Заголовок карточки */
.distributor-card .card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  border-radius: 12px 12px 0 0 !important;
}

.current-distributor .card-header {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
}

.distributor-name {
  color: #343a40;
  font-weight: 600;
  font-size: 1rem;
}

.current-badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.3rem 0.6rem;
  border-radius: 12px;
}

/* Информация о дистрибьюторе */
.distributor-info {
  padding: 0.5rem 0;
}

.info-item {
  display: flex;
  align-items: flex-start;
  font-size: 0.875rem;
  line-height: 1.4;
  font-weight: normal;
}

.info-item strong {
  font-weight: 500;
}

.info-item .bi {
  margin-top: 0.1rem;
  margin-right: 0.5rem;
  flex-shrink: 0;
}

.address {
  color: #6c757d;
  line-height: 1.4;
  font-weight: normal;
}

/* Секция контактов */
.contacts-section {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
}

.contact-item {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: normal;
}

.contact-item .bi {
  margin-right: 0.5rem;
}

.contact-link {
  color: #495057;
  text-decoration: none;
  transition: color 0.2s ease;
  font-weight: normal;
}

.contact-link:hover {
  color: #007bff;
  text-decoration: underline;
}

/* Футер карточки */
.distributor-card .card-footer {
  background: transparent;
  border-top: 1px solid #dee2e6;
  padding: 1rem 1.25rem;
}

.distributor-card .card-footer .btn {
  font-size: 0.875rem;
  font-weight: 500;
}

/* Дополнительная информация */
.additional-info {
  margin-top: 3rem;
}

.info-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.info-card .card-header {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  border-radius: 12px 12px 0 0 !important;
  border-bottom: none;
}

.info-card .card-header h5 {
  font-size: 1rem;
  font-weight: 600;
}

.info-card h6 {
  font-size: 0.9rem;
  font-weight: 600;
}

.feature-list {
  list-style: none;
  padding: 0;
}

.feature-list li {
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: normal;
}

.feature-list .bi {
  flex-shrink: 0;
  margin-right: 0.5rem;
}

/* Адаптивность */
@media (max-width: 768px) {
  .distributors-page {
    padding: 0.5rem;
  }

  .service-description {
    padding: 1rem;
  }

  .distributor-card {
    margin-bottom: 1.5rem;
  }

  .distributors-header {
    margin-bottom: 1.5rem;
  }

  .additional-info {
    margin-top: 2rem;
  }
}

/* Анимации */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.distributor-card {
  animation: fadeIn 0.5s ease-out;
}

.distributor-card:nth-child(1) {
  animation-delay: 0.1s;
}

.distributor-card:nth-child(2) {
  animation-delay: 0.2s;
}

.distributor-card:nth-child(3) {
  animation-delay: 0.3s;
}

.distributor-card:nth-child(4) {
  animation-delay: 0.4s;
}
</style>
